'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function CapabilitiesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-20 md:py-28 border-t border-border relative"
      aria-label="ForensicDesk core capabilities"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className={`mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="bracket-tag text-primary">[CORE CAPABILITIES // FOUR PILLARS]</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-foreground leading-tight max-w-xl">
            The Complete<br />
            <span className="text-gradient-crimson">Intelligence Stack.</span>
          </h2>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

          {/* Card: Forensics — col-span-2, row-span-2 */}
          <article
            className={`lg:col-span-2 lg:row-span-2 group relative glass-card border border-border p-6 md:p-8 capability-card-hover flex flex-col justify-between min-h-[320px] lg:min-h-[440px] transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="absolute top-0 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" aria-hidden="true" />

            <div>
              <div className="flex items-start justify-between mb-6">
                <span className="bracket-tag text-primary">[PILLAR 01]</span>
                <Icon name="MagnifyingGlassIcon" size={20} className="text-muted-foreground group-hover:text-primary transition-colors duration-300" />
              </div>

              <h3 className="text-3xl md:text-4xl font-black tracking-tighter uppercase text-foreground leading-tight mb-4 whitespace-pre-line">
                {'Blockchain\nForensics'}
              </h3>

              <p className="text-muted-foreground leading-relaxed font-light mb-6 max-w-lg">
                On-chain transaction graph analysis across BTC, ETH, USDT (ERC-20/TRC-20), SOL,
                and 40+ networks. We reconstruct fund flow from initial theft to mixer obfuscation
                to off-ramp exchange withdrawal.
              </p>

              <div className="grid grid-cols-2 gap-2">
                {['UTXO cluster analysis', 'Cross-chain bridge tracking', 'Mixer de-anonymization', 'Exchange subpoena packages']?.map((d) => (
                  <div key={d} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-1 h-1 bg-accent shrink-0" />
                    <span className="font-medium uppercase tracking-wide">{d}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
              <span className="bracket-tag text-muted-foreground">[BTC / ETH / USDT / SOL / +40 CHAINS]</span>
              <div className="flex items-center gap-2 text-accent">
                <span className="w-1.5 h-1.5 rounded-full bg-accent threat-pulse" />
                <span className="bracket-tag text-accent">OPERATIONAL</span>
              </div>
            </div>
          </article>

          {/* Card: Syndicate Recon — col-span-1 */}
          <article
            className={`group relative glass-card border border-border p-5 md:p-6 capability-card-hover flex flex-col justify-between min-h-[200px] transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="absolute top-0 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" aria-hidden="true" />

            <div>
              <div className="flex items-start justify-between mb-4">
                <span className="bracket-tag text-primary">[PILLAR 02]</span>
                <Icon name="GlobeAltIcon" size={16} className="text-muted-foreground group-hover:text-primary transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-black tracking-tighter uppercase text-foreground leading-tight mb-3 whitespace-pre-line">
                {'Syndicate\nReconnaissance'}
              </h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Phishing infrastructure mapping and threat actor profiling. We identify the operators—not just the wallets.
              </p>
            </div>

            <div className="mt-4 flex flex-col gap-1">
              {['Domain clustering', 'Phishing kit fingerprinting', 'Operator attribution']?.map((d) => (
                <div key={d} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-1 h-1 bg-accent shrink-0" />
                  <span className="font-medium uppercase tracking-wide">{d}</span>
                </div>
              ))}
            </div>
          </article>

          {/* Card: Asset Triage — col-span-1 */}
          <article
            className={`group relative glass-card border border-border p-5 md:p-6 capability-card-hover flex flex-col justify-between min-h-[200px] transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="absolute top-0 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" aria-hidden="true" />

            <div>
              <div className="flex items-start justify-between mb-4">
                <span className="bracket-tag text-primary">[PILLAR 03]</span>
                <Icon name="ShieldCheckIcon" size={16} className="text-muted-foreground group-hover:text-primary transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-black tracking-tighter uppercase text-foreground leading-tight mb-3 whitespace-pre-line">
                {'Asset Triage\n& Recovery'}
              </h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed">
                Court-ready evidentiary packages for law enforcement referral and international asset freezing.
              </p>
            </div>

            <div className="mt-4 flex flex-col gap-1">
              {['Chain-of-custody docs', 'Expert witness packages', 'Exchange freeze requests']?.map((d) => (
                <div key={d} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-1 h-1 bg-accent shrink-0" />
                  <span className="font-medium uppercase tracking-wide">{d}</span>
                </div>
              ))}
            </div>
          </article>

          {/* Card: Verification Tool — col-span-3 (full width) */}
          <article
            className={`lg:col-span-3 group relative glass-card border border-border p-5 md:p-6 capability-card-hover flex flex-col gap-4 min-h-[160px] transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '400ms' }}
          >
            <div className="absolute top-0 left-0 w-full h-px bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" aria-hidden="true" />

            <div className="flex items-start gap-4 flex-1">
              <div className="shrink-0 mt-1">
                <Icon name="BeakerIcon" size={20} className="text-muted-foreground group-hover:text-primary transition-colors duration-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="bracket-tag text-primary">[PILLAR 04]</span>
                </div>
                <h3 className="text-xl font-black tracking-tighter uppercase text-foreground leading-tight mb-2">
                  Verification Tool
                </h3>
                <p className="text-sm text-muted-foreground font-light leading-relaxed">
                  Instant threat analysis for crypto addresses, URLs, and email addresses. Submit any indicator of compromise for real-time intelligence scoring, risk classification, and actionable threat context.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-border">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {['Crypto address lookup', 'URL threat scoring', 'Email risk analysis']?.map((d) => (
                  <div key={d} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-1 h-1 bg-accent shrink-0" />
                    <span className="font-medium uppercase tracking-wide">{d}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-accent threat-pulse" />
                <span className="bracket-tag text-accent">OPERATIONAL</span>
              </div>
            </div>
          </article>

        </div>
      </div>
    </section>
  );
}